import styled from 'styled-components';
import { CheckmarkIc, RemoveIc, HelpIc } from '@dsch/dd-icons';
import { useUserContext } from '../../../../../contexts/UserContext';
import { media } from '../../../../../helpers/breakpoints';
import { DetailPanel } from '../../../../Toolkit/DetailPanel/DetailPanel';
import { Button } from '../../../../Toolkit/Button';
import {
  CDN_STATIC_ASSETS,
  FEATURE_FREE_HISTORY_CHECK,
} from '../../../../../utils';
import type {
  ListBlock,
  TitleGroup,
  InfoLink,
} from '../../../../../api/types/greenlightTypes';

export interface GreenlightPanelProps {
  listBlock?: ListBlock;
  titleGroup: TitleGroup;
  infoLink?: InfoLink;
  greenlightVerified?: boolean;
  showHistoryCheck?: boolean;
  className?: string;
  dataTestid?: string;
}

const SGreenlightPanel = styled.div<{ extraInfo: boolean }>`
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.default} ${theme.borderRadius.default} 0 0`};
  ${media.large} {
    ${({ theme, extraInfo }) =>
      extraInfo && `padding-bottom: ${theme.spacing.M16}`};
  }
`;

const SDetailPanel = styled(DetailPanel)<{ extraInfo: boolean }>`
  margin-bottom: ${({ theme, extraInfo }) => extraInfo && theme.spacing.M16};

  ${media.large} {
    margin-bottom: ${({ theme, extraInfo }) => extraInfo && theme.spacing.M24};
  }
`;
const ExtraInfoV1 = styled.p`
  ${({ theme }) => theme.fontSize.B14};
`;
const ExtraInfo = styled.div`
  font-size: ${({ theme }) => theme.donedeal.fontSize.B14};
  background-color: ${({ theme }) => theme.donedeal.colors.OFFWHITE};
  padding: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARKER};
  border-radius: 4px;
  line-height: 24px;
`;

const HistoryCheckWhitePanel = styled.div`
  border-radius: ${({ theme }) =>
    `0 0 ${theme.borderRadius.default} ${theme.borderRadius.default}`};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SCheckmarkIc = styled(CheckmarkIc)`
  color: ${({ theme }) => theme.colors.GREEN};
`;

const SIoRemove = styled(RemoveIc)`
  color: ${({ theme }) => theme.colors.GREEN};
`;

const SHelpIC = styled(HelpIc)`
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARK};
`;

const NeutralIoRemove = styled(RemoveIc)`
  color: ${({ theme }) => theme.colors.GREY};
`;

function iconSelector(iconType: string | undefined) {
  switch (iconType) {
    case 'icon-check-positive-tick':
      return (
        <SCheckmarkIc width="16" height="16" data-testid="checkmark-icon" />
      );
    case 'icon-check-positive-dash':
      return (
        <SIoRemove width="16" height="16" data-testid="positive-remove-icon" />
      );
    case 'icon-check-question-mark':
      return (
        <SHelpIC width="16" height="16" data-testid="indeterminate-icon" />
      );
    default:
      return (
        <NeutralIoRemove
          width="16"
          height="16"
          data-testid="neutral-remove-icon"
        />
      );
  }
}

const GreenlightPanel = (props: GreenlightPanelProps) => {
  const {
    titleGroup,
    infoLink,
    listBlock,
    greenlightVerified,
    showHistoryCheck,
    className,
    dataTestid,
  } = props;

  const { setIsHistoryCheckModalOpen } = useUserContext();
  const filteredValues = listBlock?.items.map((item) => {
    return {
      title: item.title,
      body: item.body,
      icon: iconSelector(item.icon),
    };
  });

  return (
    <SGreenlightPanel
      extraInfo={Boolean(listBlock?.extraInfo)}
      className={className}
      data-testid={dataTestid}
    >
      {titleGroup.title && filteredValues && (
        <SDetailPanel
          title={titleGroup.title}
          illustrationUrl={`${CDN_STATIC_ASSETS}/images/illustrations/greenlight${
            !greenlightVerified ? '-unverified' : ''
          }.svg`}
          detailListItems={
            FEATURE_FREE_HISTORY_CHECK
              ? listBlock?.extraInfo
                ? null
                : filteredValues
              : filteredValues
          }
          listItems={[titleGroup.subtitle]}
          link={infoLink}
          extraInfo={Boolean(listBlock?.extraInfo)}
          detailListVariant="MEDIUM"
        />
      )}
      {listBlock?.extraInfo && FEATURE_FREE_HISTORY_CHECK ? (
        <ExtraInfo>{listBlock.extraInfo.text}</ExtraInfo>
      ) : (
        <ExtraInfoV1>{listBlock?.extraInfo?.text}</ExtraInfoV1>
      )}
      {showHistoryCheck && (
        <HistoryCheckWhitePanel>
          <Button
            onClick={() => setIsHistoryCheckModalOpen(true)}
            ofType="SECONDARY"
          >
            Get full history for €25
          </Button>
        </HistoryCheckWhitePanel>
      )}
    </SGreenlightPanel>
  );
};

export { GreenlightPanel };
